import { Stack, Typography } from '@mui/material';

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime, formatDateTimeInTimeZone } from 'utils/date.utils';
import { Message, MessageDirections } from '../campaign-statistics/campaign-statistics-types';
import { MessageTypes } from './funnel-inbox.types';

interface ItemProps {
  message: Message;
  timeZone: string | undefined;
}

const MessageItem: React.FC<ItemProps> = ({ message, timeZone }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const isSent = message.direction === MessageDirections.SENT;

  return (
    <Stack className={cx(classes.messageRow, message.direction)} gap={'6px'}>
      <div className={cx(classes.messageBubble, message.direction)}>
        <Typography className={classes.regularText}>{message.text}</Typography>
      </div>
      <Typography className={classes.greyText} sx={{ textAlign: isSent ? 'right' : 'left' }}>
        {`${timeZone ? formatDateTimeInTimeZone(message.date, 'Pp', timeZone) : formatDateTime(message.date, 'Pp')} ${t('from')} ${message.messageType === MessageTypes.EMAIL ? t('emailShort') : 'LinkedIn'
          }`}
      </Typography>
    </Stack>
  );
};

interface Props {
  messages: Message[];
  timeZone: string | undefined;
}

const ChatMessages: React.FC<Props> = ({ messages, timeZone }) => {
  const { classes } = useStyles();

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'instant' });
    }
  }, [messages]);

  return (
    <Stack className={classes.chatMessages} gap={'18px'}>
      {messages.map((message, index) => (
        <MessageItem key={index} message={message} timeZone={timeZone} />
      ))}
      <div ref={messagesEndRef} />
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  chatMessages: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    padding: '24px',
    background: theme.palette.backgrounds.main_new,
  },
  messageRow: {
    maxWidth: '62%',
    '&.sent': {
      marginLeft: 'auto',
    },
  },
  messageBubble: {
    padding: '12px 16px',
    borderRadius: '12px',
    '&.sent': {
      background: theme.palette.backgrounds.main,
      border: '1px solid #DCD9FF',
    },
    '&.received': {
      background: '#FFF',
      border: `1px solid ${theme.palette.greys.lighter}`,
    },
  },
  regularText: {
    fontSize: theme.typography.pxToRem(15),
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(12),
    whiteSpace: 'pre-wrap',
    color: theme.palette.greys.medium,
  },
}));

export default ChatMessages;
